import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ShoppingBag, Heart } from "lucide-react";
import StarRating from "@/components/StarRating";
import { resolveImageUrl } from "@/components/ImageUploader";

const ProductCard = ({ product, index = 0 }) => {
  const { lang, t } = useLang();
  const { add } = useCart();
  const { has, toggle } = useWishlist();
  const { format } = useCurrency();
  const isWished = has(product.id);
  const img = resolveImageUrl((product.images || [])[0]);
  const name = lang === "el" ? product.name_el : product.name_en;
  const onSale = product.sale_price && product.sale_price < product.price;

  return (
    <div
      className="group fade-up relative"
      style={{ animationDelay: `${index * 0.06}s` }}
      data-testid={`product-card-${product.id}`}
    >
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(product.id); }}
        className={`absolute top-3 right-3 z-10 h-9 w-9 rounded-full flex items-center justify-center backdrop-blur transition-all ${
          isWished ? "bg-white text-[#8B0000]" : "bg-white/80 text-[#2C1E16] hover:bg-white hover:text-[#8B0000]"
        }`}
        data-testid={`wishlist-toggle-${product.id}`}
        aria-label={isWished ? "Remove from wishlist" : "Add to wishlist"}
      >
        <Heart size={16} fill={isWished ? "#8B0000" : "transparent"} strokeWidth={1.8} />
      </button>
      <Link to={`/product/${product.id}`} className="block">
        <div className="aspect-[4/5] bg-[#F5F2ED] overflow-hidden mb-4 relative">
          {img && (
            <img
              src={img}
              alt={name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
          )}
          {onSale && (
            <span className="absolute top-3 left-3 bg-[#8B0000] text-white text-[10px] tracking-widest uppercase px-3 py-1 font-semibold">
              {t("product.sale")}
            </span>
          )}
        </div>
        <div className="space-y-1">          {product.brand && (
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#736B66]">{product.brand}</p>
          )}
          <h3 className="font-serif text-lg leading-tight text-[#2C1E16] group-hover:text-[#D4AF37] transition-colors line-clamp-2">
            {name}
          </h3>
          {product.review_count > 0 && (
            <div className="flex items-center gap-1.5 pt-1">
              <StarRating value={product.avg_rating || 0} size="sm" />
              <span className="text-xs text-[#736B66]">({product.review_count})</span>
            </div>
          )}
          <div className="flex items-baseline gap-2 pt-1">
            {onSale ? (
              <>
                <span className="text-base font-semibold text-[#8B0000]">{format(product.sale_price)}</span>
                <span className="text-xs text-[#736B66] line-through">{format(product.price)}</span>
              </>
            ) : (
              <span className="text-base font-semibold">{format(product.price)}</span>
            )}
          </div>
        </div>
      </Link>
      <button
        onClick={() => add(product)}
        className="mt-4 w-full opacity-0 group-hover:opacity-100 transition-opacity luxury-btn-outline text-xs"
        data-testid={`add-to-cart-${product.id}`}
      >
        <ShoppingBag size={14} />
        {t("product.addToCart")}
      </button>
    </div>
  );
};

export default ProductCard;
